/**
 * Stage 25 · Technology Economics. Estate spend + budgets + variance +
 * optimisation opportunities + savings register + CFO summary.
 */
import { orgLabel } from "@/lib/orgLabel";
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, PiggyBank, TrendingUp, Sparkles, Plus, Lock } from "lucide-react";

const TABS = [
  { key: "cfo",       label: "CFO summary",  icon: Wallet },
  { key: "budgets",   label: "Budgets",      icon: PiggyBank },
  { key: "variance",  label: "Variance",     icon: TrendingUp },
  { key: "opps",      label: "Opportunities",icon: Sparkles },
  { key: "savings",   label: "Savings",      icon: Lock },
];

export default function Economics() {
  const [tab, setTab] = useState("cfo");
  const [masters, setMasters] = useState([]);
  const [mc, setMc] = useState("");
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => { setMe(data); if (data?.master_company_id) setMc(data.master_company_id); });
    api.get("/master-companies").then(({ data }) => { setMasters(data.items || []); setMc(current => current || data.items?.[0]?.id || ""); });
  }, []);
  const isSuntronix = me && !me.master_company_id;
  return (
    <div className="max-w-6xl space-y-5" data-testid="economics">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Insights · Stage 25</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Technology Economics</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          What technology do we have · what does it cost · who consumes it · where can cost be optimised. Every number linked to real records; estimates are labelled.
        </p>
      </div>
      {isSuntronix && (
        <select className="input min-w-[240px]" value={mc} onChange={(e) => setMc(e.target.value)} data-testid="econ-tenant">
          {masters.map((m) => <option key={m.id} value={m.id}>{orgLabel(m, masters)}</option>)}
        </select>
      )}
      <nav className="flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`econ-tab-${key}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
              (tab === key ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>
      {mc && (
        <>
          {tab === "cfo"      && <CFOSummary mc={mc} />}
          {tab === "budgets"  && <BudgetsTab mc={mc} isSuntronix={isSuntronix} />}
          {tab === "variance" && <VarianceTab mc={mc} />}
          {tab === "opps"     && <OppsTab mc={mc} isSuntronix={isSuntronix} />}
          {tab === "savings"  && <SavingsTab mc={mc} isSuntronix={isSuntronix} />}
        </>
      )}
    </div>
  );
}

function CFOSummary({ mc }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (!mc) return; api.get("/economics/cfo-summary", { params: { master_company_id: mc } }).then(({ data }) => setD(data)); }, [mc]);
  if (!d) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="econ-cfo">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Invoiced (90d)" value={fmtINR(d.estate.spend.invoiced_total_inr)} />
        <Tile label="Paid"           value={fmtINR(d.estate.spend.invoiced_paid_inr)} tone="ok" />
        <Tile label="Outstanding"    value={fmtINR(d.estate.spend.outstanding_inr)} tone="warn" />
        <Tile label="Realised savings" value={fmtINR(d.realised_savings_inr)} tone="ok" />
        <Tile label="Assets"       value={d.estate.operational.assets_total} />
        <Tile label="Active orders" value={d.estate.operational.active_orders} />
        <Tile label="Unassigned"   value={d.estate.unused_capital.unassigned_assets} />
        <Tile label="Aged 90d"     value={d.estate.unused_capital.aged_inventory_90d} tone={d.estate.unused_capital.aged_inventory_90d ? "warn" : undefined} />
      </div>
      <VarianceCard v={d.variance} />
      <OppsList items={d.opportunities.items} />
    </div>
  );
}

function BudgetsTab({ mc, isSuntronix }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/economics/budgets", { params: { master_company_id: mc } }).then(({ data }) => setItems(data.items || [])), [mc]);
  useEffect(() => { load(); }, [load, mc]);
  return (
    <div className="space-y-3" data-testid="econ-budgets">
      {isSuntronix && (
        <div className="flex justify-end">
          <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="econ-budget-new">
            <Plus className="h-4 w-4" /> New budget
          </button>
        </div>
      )}
      {showForm && <BudgetForm mc={mc} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Kind</th><th className="text-left py-2 px-3">Period</th><th className="text-left py-2 px-3">Dimension</th><th className="text-right py-2 px-3">Annual (INR)</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={4} className="py-6 text-center text-slate-500">No budgets yet.</td></tr>
            ) : items.map((b) => (
              <tr key={b.id} className="border-t border-slate-100">
                <td className="py-2 px-3 text-slate-700">{b.kind?.replace(/_/g, " ")}</td>
                <td className="py-2 px-3 text-slate-600 font-mono">{b.period_key}</td>
                <td className="py-2 px-3 text-slate-600">{b.dimension || "—"}</td>
                <td className="py-2 px-3 text-right tabular-nums">{fmtINR(b.annual_inr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BudgetForm({ mc, onCancel, onCreated }) {
  const [f, setF] = useState({ kind: "global_it", period: "annual", period_key: String(new Date().getFullYear()), dimension: "", annual_inr: 0 });
  const save = async () => {
    try { await api.post("/economics/budgets", { master_company_id: mc, ...f }); toast.success("Budget created"); onCreated(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="econ-budget-form">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Kind</div>
          <select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="econ-budget-kind">
            {["global_it","country","department","cost_centre","project","refresh","employee_technology","service"].map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
          </select></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Period key</div>
          <input className="input" value={f.period_key} onChange={(e) => setF({ ...f, period_key: e.target.value })} data-testid="econ-budget-period" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Dimension</div>
          <input className="input" value={f.dimension} onChange={(e) => setF({ ...f, dimension: e.target.value })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Annual (INR)</div>
          <input type="number" className="input" value={f.annual_inr} onChange={(e) => setF({ ...f, annual_inr: Number(e.target.value) })} data-testid="econ-budget-annual" /></label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="econ-budget-save">Save</button>
      </div>
    </div>
  );
}

function VarianceTab({ mc }) {
  const [v, setV] = useState(null);
  useEffect(() => { if (!mc) return; api.get("/economics/variance", { params: { master_company_id: mc } }).then(({ data }) => setV(data)); }, [mc]);
  if (!v) return <div className="text-sm text-slate-500">Loading…</div>;
  return <VarianceCard v={v} />;
}
function VarianceCard({ v }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-4 space-y-3" data-testid="econ-variance">
      <h3 className="text-sm font-medium text-slate-900">Variance · {v.period_key}</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Budget"    value={fmtINR(v.budget_annual_inr)} />
        <Tile label="Committed" value={fmtINR(v.committed_inr)} />
        <Tile label="Actual"    value={fmtINR(v.actual_inr)} tone="ok" />
        <Tile label="Forecast"  value={fmtINR(v.forecast_estimate_inr)} />
        <Tile label="Remaining" value={fmtINR(v.remaining_inr)} tone={v.remaining_inr < 0 ? "warn" : undefined} />
        <Tile label="Utilised %" value={v.utilisation_pct ?? "—"} />
      </div>
      <div className="text-[10px] text-slate-500 uppercase tracking-widest">{(v.labels || []).join(" · ")}</div>
    </section>
  );
}

function OppsTab({ mc, isSuntronix }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/economics/opportunities", { params: { master_company_id: mc } }).then(({ data }) => setItems(data.items || [])); }, [mc]);
  return <OppsList items={items} />;
}
function OppsList({ items }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md" data-testid="econ-opps">
      <div className="p-3 border-b border-slate-200 text-sm font-medium text-slate-900">Optimisation opportunities</div>
      {items.length === 0 ? (
        <div className="p-4 text-sm text-slate-500">Nothing to optimise from recorded data right now.</div>
      ) : (
        <ul className="divide-y">
          {items.map((o) => (
            <li key={o.key} className="p-3">
              <div className="text-sm text-slate-900 font-medium">{o.title}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">{o.kind}</div>
              <pre className="mt-1 text-[10px] text-slate-500 bg-slate-50 rounded p-1 inline-block">{JSON.stringify(o.evidence)}</pre>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SavingsTab({ mc, isSuntronix }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const load = useCallback(() => api.get("/economics/savings", { params: { master_company_id: mc } }).then(({ data }) => setItems(data.items || [])), [mc]);
  useEffect(() => { load(); }, [load, mc]);
  const advance = async (id, status, realised) => {
    try { await api.patch(`/economics/savings/${id}`, { status, realised_inr: realised }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="econ-savings">
      {isSuntronix && (
        <div className="flex justify-end">
          <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="econ-saving-new">
            <Plus className="h-4 w-4" /> New saving
          </button>
        </div>
      )}
      {showForm && <SavingForm mc={mc} onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Title</th><th className="text-left py-2 px-3">Status</th><th className="text-right py-2 px-3">Potential</th><th className="text-right py-2 px-3">Realised</th><th className="text-right py-2 px-3">Actions</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-center text-slate-500">No savings tracked.</td></tr>
            ) : items.map((s) => (
              <tr key={s.id} className="border-t border-slate-100">
                <td className="py-2 px-3">
                  <div className="text-slate-900">{s.title}</div>
                  <div className="text-[10px] text-slate-500">{s.methodology}</div>
                </td>
                <td className="py-2 px-3 text-slate-600">{s.status}</td>
                <td className="py-2 px-3 text-right tabular-nums">{fmtINR(s.potential_inr)}</td>
                <td className="py-2 px-3 text-right tabular-nums text-emerald-700">{fmtINR(s.realised_inr)}</td>
                <td className="py-2 px-3 text-right text-xs">
                  {isSuntronix && s.status !== "realised" && (
                    <>
                      {s.status === "identified" && <button onClick={() => advance(s.id, "approved")} className="px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">Approve</button>}
                      {s.status === "approved" && <button onClick={() => advance(s.id, "implemented")} className="px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">Implement</button>}
                      {s.status === "implemented" && <button onClick={() => advance(s.id, "realised", s.potential_inr)} className="px-2 py-0.5 rounded bg-emerald-50 border-emerald-200 border text-emerald-800">Mark realised</button>}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function SavingForm({ mc, onCancel, onCreated }) {
  const [f, setF] = useState({ title: "", methodology: "", potential_inr: 0, description: "", status: "identified" });
  const save = async () => {
    try { await api.post("/economics/savings", { master_company_id: mc, ...f }); toast.success("Saving logged"); onCreated(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="econ-saving-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Title</div>
          <input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="econ-saving-title" /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Methodology</div>
          <input className="input" value={f.methodology} onChange={(e) => setF({ ...f, methodology: e.target.value })} placeholder="How was potential calculated?" data-testid="econ-saving-method" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Potential (INR)</div>
          <input type="number" className="input" value={f.potential_inr} onChange={(e) => setF({ ...f, potential_inr: Number(e.target.value) })} /></label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="econ-saving-save">Save</button>
      </div>
    </div>
  );
}

function Tile({ label, value, tone }) {
  const cls = tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"mt-1 text-xl font-heading font-semibold tabular-nums " + cls}>{value ?? "—"}</div>
    </div>
  );
}
function fmtINR(n) { return n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN"); }
